import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";

export const TicketCodeValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    resourceId: t.string,
    siteId: t.string,
    wellStageId: t.string,
    invoiceAccountLookupId: t.string,
    authorizorId: t.string,
    engineerId: t.string,
    disputed: t.boolean,
    amountPercent: t.number,
    approvedDttm: t.number,
  }),
  t.partial({
    code: t.string,
    afe: t.string,
    afePrefix: t.string,
    afeSuffix: t.string,
    siteName: t.string,
    wellId: t.string,
    wellName: t.string,
    stage: t.string,
    stageCode: t.string,
    subStage: t.string,
    subStageCode: t.string,
    invoiceAccountLookupName: t.string,
    authorizorName: t.string,
    engineerName: t.string,
    rateModelType: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    //
    comment: t.string,
    database: t.string,
  }),
]);

export const TicketCodeArrayValidator = t.array(
  TicketCodeValidator
);

export type TicketCode = t.TypeOf<
  typeof TicketCodeValidator
>;
export type TicketCodeArray = t.TypeOf<
  typeof TicketCodeArrayValidator
>;

@Exclude()
export class TicketCodeEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string

  @Expose()
  objectType!: string

  @Expose()
  resourceId!: string

  @Expose()
  amountPercent!: number

  @Expose()
  disputed!: boolean

  @Expose()
  code?: string

  @Expose()
  afe?: string

  @Expose()
  afePrefix?: string

  @Expose()
  afeSuffix?: string

  @Expose()
  siteId!: string

  @Expose()
  siteName?: string

  @Expose()
  wellId?: string

  @Expose()
  wellName?: string

  @Expose()
  wellStageId!: string

  @Expose({name: "stage"})
  wellStageName?: string

  @Expose({name: "stageCode"})
  wellStageCode?: string

  @Expose({name: "subStage"})
  wellSubStageName?: string

  @Expose({name: "subStageCode"})
  wellSubStageCode?: string

  @Expose({name: "invoiceAccountLookupId"})
  invoiceAccountId!: string

  @Expose({name: "invoiceAccountLookupName"})
  invoiceAccountName?: string

  @Expose({name: "authorizorId"})
  authorizerId!: string

  @Expose({name: "authorizorName"})
  authorizerName?: string

  @Expose()
  engineerId!: string

  @Expose()
  engineerName?: string

  @Expose()
  rateModelType?: string

  @Expose()
  approvedDttm?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  comment?: string

  @Expose()
  database?: string

  readonly entityType = 'TICKETCODE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for TicketCodeEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(
    ticketCode: TicketCode
  ): TicketCodeEntity {
    return plainToInstance(TicketCodeEntity, ticketCode, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<TicketCode> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(TicketCodeValidator, object);
  }

  asTypeValue(): TicketCode {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TicketCodeValidator, object);
  }
}
